import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Briefcase, TrendingUp, Clock, Star, Brain, FileSignature, BarChart3, MessageSquare, ArrowLeft, ChevronRight, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const DEMO_CANDIDATES = [
  { name: 'Sarah Martinez', role: 'Companion Caregiver', score: 92, status: 'approved', stars: 5, applied: '2 days ago' },
  { name: 'James Wilson', role: 'CHHA Caregiver', score: 85, status: 'interviewed', stars: 4, applied: '3 days ago' },
  { name: 'Maria Lopez', role: 'Companion Caregiver', score: 78, status: 'pending_review', stars: 4, applied: '5 days ago' },
  { name: 'David Chen', role: 'Companion Caregiver', score: 71, status: 'new', stars: 3, applied: '1 week ago' },
  { name: 'Emily Thompson', role: 'CHHA Caregiver', score: 88, status: 'offer_sent', stars: 5, applied: '1 week ago' },
];

const DEMO_STATS = {
  total_candidates: 47,
  active_jobs: 3,
  avg_score: 76,
  avg_time_to_hire: '8 days',
  this_week_applications: 12,
  interviews_scheduled: 5,
  offers_pending: 2,
};

const statusColors = {
  new: 'bg-gray-100 text-gray-700',
  pending_review: 'bg-yellow-100 text-yellow-700',
  interviewed: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  offer_sent: 'bg-purple-100 text-purple-700',
};

const statusLabels = {
  new: 'New',
  pending_review: 'Under Review',
  interviewed: 'Interviewed',
  approved: 'Approved',
  offer_sent: 'Offer Sent',
};

const DemoPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Demo Banner */}
      <div className="bg-[#E8722A] text-white text-center py-2.5 px-4 text-sm font-medium">
        You're viewing the SeniorPeerConnect™ interactive demo with sample data.
        <Button variant="link" className="text-white underline ml-2 p-0 h-auto" onClick={() => navigate('/')} data-testid="exit-demo-btn">
          Exit Demo
        </Button>
      </div>

      {/* Sidebar + Content */}
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-[#E5E5E5] min-h-[calc(100vh-40px)] p-6 hidden md:block">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#4B2D6E] rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 text-white" />
            </div>
            <span className="font-[Outfit] font-bold text-lg text-[#1A1A1A]">SPC<span className="text-[#E8722A]">™</span></span>
          </div>
          <p className="text-xs text-[#999] uppercase tracking-wider mb-4 font-semibold">Demo Territory</p>
          <p className="text-sm font-medium text-[#4B2D6E] mb-6">Central New Jersey</p>

          <nav className="space-y-1">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'candidates', label: 'Candidates', icon: Users },
              { id: 'jobs', label: 'Jobs', icon: Briefcase },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp },
            ].map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                  activeTab === item.id
                    ? 'bg-[#4B2D6E]/10 text-[#4B2D6E] font-semibold'
                    : 'text-[#595959] hover:bg-[#F5F5F5]'
                }`}
                data-testid={`demo-nav-${item.id}`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8 max-w-6xl">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6" data-testid="demo-dashboard">
              <div>
                <h1 className="text-2xl font-bold text-[#1A1A1A] font-[Outfit]">Welcome to SeniorPeerConnect™</h1>
                <p className="text-[#595959] mt-1">Here's what your franchise dashboard looks like with real activity.</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Candidates', value: DEMO_STATS.total_candidates, icon: Users, color: 'text-[#4B2D6E]' },
                  { label: 'Active Jobs', value: DEMO_STATS.active_jobs, icon: Briefcase, color: 'text-[#E8722A]' },
                  { label: 'Avg AI Score', value: DEMO_STATS.avg_score, icon: Brain, color: 'text-green-600' },
                  { label: 'Avg Time to Hire', value: DEMO_STATS.avg_time_to_hire, icon: Clock, color: 'text-blue-600' },
                ].map((stat, i) => (
                  <Card key={i} className="border border-[#E5E5E5]">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <stat.icon className={`h-5 w-5 ${stat.color}`} />
                        <span className="text-xs text-[#999]">This month</span>
                      </div>
                      <p className="text-2xl font-bold text-[#1A1A1A]">{stat.value}</p>
                      <p className="text-xs text-[#595959] mt-1">{stat.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Recent Candidates */}
              <Card className="border border-[#E5E5E5]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-[Outfit]">Recent Candidates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {DEMO_CANDIDATES.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-lg border border-[#E5E5E5] hover:border-[#4B2D6E]/30 transition-colors cursor-pointer">
                        <div className="flex items-center gap-4">
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                            c.score >= 90 ? 'bg-green-500' : c.score >= 80 ? 'bg-[#E8722A]' : 'bg-[#4B2D6E]'
                          }`}>
                            {c.score}
                          </div>
                          <div>
                            <p className="font-semibold text-[#1A1A1A] text-sm">{c.name}</p>
                            <p className="text-xs text-[#595959]">{c.role}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="hidden sm:flex gap-0.5">
                            {Array.from({ length: c.stars }).map((_, j) => (
                              <Star key={j} className="h-3 w-3 fill-[#E8722A] text-[#E8722A]" />
                            ))}
                          </div>
                          <Badge className={statusColors[c.status]}>{statusLabels[c.status]}</Badge>
                          <span className="text-xs text-[#999] hidden md:inline">{c.applied}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border border-[#E5E5E5] hover:border-[#4B2D6E]/30 transition-colors cursor-pointer">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#4B2D6E]/10 rounded-lg flex items-center justify-center">
                      <Brain className="h-5 w-5 text-[#4B2D6E]" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">AI Screening</p>
                      <p className="text-xs text-[#595959]">Auto-scores every applicant</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-[#E5E5E5] hover:border-[#4B2D6E]/30 transition-colors cursor-pointer">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#E8722A]/10 rounded-lg flex items-center justify-center">
                      <FileSignature className="h-5 w-5 text-[#E8722A]" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Offer Letters</p>
                      <p className="text-xs text-[#595959]">Templates + e-signatures</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-[#E5E5E5] hover:border-[#4B2D6E]/30 transition-colors cursor-pointer">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">SMS & Email</p>
                      <p className="text-xs text-[#595959]">Automated notifications</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Candidates Tab */}
          {activeTab === 'candidates' && (
            <div className="space-y-6" data-testid="demo-candidates">
              <h1 className="text-2xl font-bold text-[#1A1A1A] font-[Outfit]">Candidate Pipeline</h1>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: 'New', count: 8, color: 'border-gray-300' },
                  { label: 'Under Review', count: 12, color: 'border-yellow-400' },
                  { label: 'Interviewed', count: 5, color: 'border-blue-400' },
                  { label: 'Approved', count: 3, color: 'border-green-400' },
                  { label: 'Hired', count: 19, color: 'border-[#4B2D6E]' },
                ].map((stage, i) => (
                  <Card key={i} className={`border-t-4 ${stage.color}`}>
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-[#1A1A1A]">{stage.count}</p>
                      <p className="text-xs text-[#595959] mt-1">{stage.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Card className="border border-[#E5E5E5]">
                <CardContent className="p-6">
                  <div className="space-y-3">
                    {DEMO_CANDIDATES.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-white rounded-lg border border-[#E5E5E5] hover:shadow-sm transition-all">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                            c.score >= 90 ? 'bg-green-500' : c.score >= 80 ? 'bg-[#E8722A]' : 'bg-[#4B2D6E]'
                          }`}>
                            {c.score}
                          </div>
                          <div>
                            <p className="font-semibold text-[#1A1A1A]">{c.name}</p>
                            <p className="text-sm text-[#595959]">{c.role} &middot; {c.applied}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={statusColors[c.status]}>{statusLabels[c.status]}</Badge>
                          <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <div className="space-y-6" data-testid="demo-jobs">
              <h1 className="text-2xl font-bold text-[#1A1A1A] font-[Outfit]">Active Job Listings</h1>
              {[
                { title: 'Companion Caregiver', applicants: 28, status: 'Active', posted: 'Mar 1, 2026' },
                { title: 'CHHA Caregiver', applicants: 14, status: 'Active', posted: 'Mar 10, 2026' },
                { title: 'Live-In Companion', applicants: 5, status: 'Active', posted: 'Mar 20, 2026' },
              ].map((job, i) => (
                <Card key={i} className="border border-[#E5E5E5] hover:border-[#4B2D6E]/30 transition-colors">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg text-[#1A1A1A]">{job.title}</h3>
                      <p className="text-sm text-[#595959]">Posted {job.posted} &middot; {job.applicants} applicants</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-green-100 text-green-700">{job.status}</Badge>
                      <Button variant="outline" size="sm">View</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6" data-testid="demo-analytics">
              <h1 className="text-2xl font-bold text-[#1A1A1A] font-[Outfit]">Hiring Analytics</h1>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border border-[#E5E5E5]">
                  <CardHeader><CardTitle className="text-base">Pipeline Conversion</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { stage: 'Applied', count: 47, pct: 100 },
                      { stage: 'Screened', count: 38, pct: 81 },
                      { stage: 'Interviewed', count: 22, pct: 47 },
                      { stage: 'Offered', count: 12, pct: 26 },
                      { stage: 'Hired', count: 9, pct: 19 },
                    ].map((s, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#595959]">{s.stage}</span>
                          <span className="font-semibold">{s.count} ({s.pct}%)</span>
                        </div>
                        <Progress value={s.pct} className="h-2" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card className="border border-[#E5E5E5]">
                  <CardHeader><CardTitle className="text-base">Key Metrics</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { label: 'Average Time to Hire', value: '8 days', desc: 'Industry avg: 42 days' },
                      { label: 'Candidate Quality Score', value: '76/100', desc: 'Above threshold' },
                      { label: 'Offer Acceptance Rate', value: '75%', desc: '9 of 12 offers accepted' },
                      { label: 'Applications This Month', value: '12', desc: '+33% vs last month' },
                    ].map((m, i) => (
                      <div key={i} className="flex justify-between items-center p-3 bg-[#F5F5F5] rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-[#1A1A1A]">{m.label}</p>
                          <p className="text-xs text-[#595959]">{m.desc}</p>
                        </div>
                        <p className="text-lg font-bold text-[#4B2D6E]">{m.value}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 bg-[#4B2D6E] rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-[Outfit] text-2xl md:text-3xl font-bold text-white mb-3">Ready to Get Started?</h2>
            <p className="text-white/70 mb-6">Contact us to set up your franchise territory on SeniorPeerConnect™</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="mailto:renee@shsnorthplainfield.com">
                <Button className="bg-[#E8722A] hover:bg-[#d0621f] text-white px-8 py-5" data-testid="demo-contact-btn">
                  Contact Us <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </a>
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-5" onClick={() => navigate('/')} data-testid="demo-back-home-btn">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DemoPage;
